import 'isomorphic-fetch';

import { receiveCurrentUser } from 'Actions/UstateActions';
import { receiveGlobalMeta, getCacheBuster, setCacheBuster } from 'Actions/MetaActions';
import {
	getFetchConfig,
	setLastGlobalAction,
	setGlobalNotification,
	backInHistory
} from 'Actions/GlobalMetaActions';
import {
	getLoadingBehavior,
	shouldNotUpdateState,
	updatePagesResult,
	fetchSuccess
} from 'Actions/InfiniteScrollActions';
import {
	showGlobalLoading,
	hideGlobalLoading,
	displayNotification
} from 'Actions/UiActions'
import { mapLoadingBehaviour, getPageSize } from 'Actions/UserSettingsActions';
import { trackGAEvent } from 'Actions/adsActions';
import { responseNormalizer, updateUstate } from 'Actions/responseNormalizer';

export const USERPROFILE_FETCH = 'USERPROFILE_FETCH';
export const USERPROFILE_SUCCESS = 'USERPROFILE_SUCCESS';
export const USERPROFILE_FAILURE = 'USERPROFILE_FAILURE';
export const RESET_USERPROFILE = 'RESET_USERPROFILE';

export function requestUserProfile( user ) {
	return {
		type: USERPROFILE_FETCH,
		user: user
	};
}

export function receiveUserProfile( user, data ) {
	return {
		type: USERPROFILE_SUCCESS,
		user: user,
		data: data
	};
}

export function failedUserProfile( user, error ) {
	return {
		type: USERPROFILE_FAILURE,
		user: user,
		error: error
	};
}

export function resetUserProfile() {
	return {
		type: RESET_USERPROFILE
	};
}

export function fetchUserProfile( userRequest, cacheBust = false ) {

	return function(dispatch, getState) {

		userRequest.loadingBehavior = getLoadingBehavior(getState, userRequest.id, "userprofile", userRequest.page, userRequest.forceRefresh);

		if ( shouldNotUpdateState( getState, userRequest ) ) {
			dispatch( updatePagesResult( userRequest.page || 1 ) );
			return null;
		}

		dispatch( requestUserProfile( userRequest ) );
		const state = getState();
		const url = state.topifyPresets.forumSite.url;

		let id = userRequest.id ? `&id=${userRequest.id}` : '',
			userID = state.ustate.id ? state.ustate.id : 0;

		// Avoid Cache if cacheBust is required
		let fetchConfig = dispatch( getFetchConfig( cacheBust ) ),
			cacheBustQueryString = '&_=' + ( cacheBust ? cacheBust : ( getCacheBuster( getState ) || 0 ) ),
			lb = mapLoadingBehaviour( state.ui.userSettings.loadingBehavior ),
			page_size = dispatch( getPageSize() );

		return fetch( url + `?v=3.0&ajax=1&uid=${ userID }&req=userprofile${id}&pagesize=${page_size}&page=${userRequest.page}&lb=${ lb }${ cacheBustQueryString }`, fetchConfig )
			.then((response) => {
				if (!response.ok) {
					try {
						response.json().then((json) => {
							setGlobalNotification( dispatch, json, response );
						});
					} catch( e ) {
						setGlobalNotification( dispatch, {}, response );
					}

					dispatch(failedUserProfile(userRequest, 'Bad response from server'));
					dispatch( backInHistory() );

				} else {
					response.json()
						.then((json) => {

							setGlobalNotification(dispatch,json,{ok: true});

							let other = Object.assign( {}, { page: userRequest.page }, json.forumUser ? json.forumUser.meta : {} );

							let result = responseNormalizer( json, userRequest.page, other );

							const forumTitle = state.topifyPresets.forumSite.f_title;

							let title = userRequest.cp === true ?
								'User Control Panel' :
								( json.ustate && json.ustate.id && parseInt( userRequest.id ) === parseInt( json.ustate.id ) ? "My" : "User" ) + " Profile";

							dispatch( receiveGlobalMeta( json.forumUser.meta, title, forumTitle ) );

							dispatch( updateUstate( json, result ) );

							// If we searched by userName instead of id
							if( typeof userRequest.id === 'string' ) {
								for( let user in result.entities.users ) {
									if( result.entities.users[ user ].userName === userRequest.id ) {
										userRequest.id = user;
										break;
									}
								}
							}

							dispatch( fetchSuccess( userRequest.id, "userprofile", userRequest.page, result.totalPages, userRequest.loadingBehavior ) );
							dispatch(receiveUserProfile(userRequest, result));
						}
					)
				}
			}
		).then(()=>{
			setLastGlobalAction('userprofile',userRequest);
		}).catch( (error) => {
			setGlobalNotification(dispatch,{},error);
			dispatch(resetUserProfile(userRequest, 'Bad response from server'));
			dispatch( backInHistory() );
		});
	};
}

export function uploadAvatar( data ) {
	return ( dispatch, getState ) => {
		dispatch( showGlobalLoading( 'Saving avatar' ) );

		const state = getState(),
			url = state.topifyPresets.forumSite.url,
			userID = state.ustate.id ? state.ustate.id : 0,
			config = dispatch( getFetchConfig( true, 'POST', data, false ) );

		return new Promise( ( resolve, reject ) => {
			return fetch( `${ url }?v=3.0&ajax=1&uid=${ userID }&req=post&type=uploadavatar`, config ).then( response => {

				dispatch( hideGlobalLoading() );

				if( !response.ok ) {
					response.json().then( json => {
						// Record the Google Analytics event.
						dispatch( trackGAEvent( false, 'avatarUploadFailed', 'error', 'system' ) );

						setGlobalNotification( dispatch, json, response );
						resolve( () => ({}) );
					} );

				} else {
					response.json().then( json => {
						displayNotification( 'success', 'The avatar was successfully uploaded', { timeout: 1000 } );
						dispatch( setCacheBuster( ( new Date().getTime() ) ) );

						if( json.forumUser.data[0] ) {
							let currentUser = json.forumUser.data[ 0 ],
								new_ustate = Object.assign( {}, json.ustate, { userName: currentUser.userName, avatar: currentUser.avatar, online: currentUser.online, lastActivity: currentUser.lastActivity } );

							dispatch( receiveCurrentUser( new_ustate ) );

							// Record the Google Analytics event.
							dispatch( trackGAEvent( false, 'uploadAvatar', 'userAction', 'userCP' ) );

							return resolve( () => ({
								result: true,
								avatar: json.forumUser.data[ 0 ].avatar
							} ) );

						} else {
							return resolve( () => ({
								result: false
							} ) );
						}
					} );
				}
			} );
		} );
	};
}

export function saveUserCP( settings ) {
	return ( dispatch, getState ) => {
		const state = getState(),
			url = state.topifyPresets.forumSite.url,
			userID = state.ustate.id ? state.ustate.id : 0;

		let fields = [],
			body = null;

		// Create the body with the fields passed
		if( Array.isArray( settings ) && settings.length ) {
			settings.forEach( ( el ) => {
				fields.push( el.join( '=' ) );
			} );

			if( fields.length ) {
				body = fields.join( '&' );
			}
		}

		let config = dispatch( getFetchConfig( true, 'POST', body ) );

		return new Promise( ( resolve, reject ) => {
			return fetch( `${ url }?v=3.0&ajax=1&uid=${ userID }&req=post&type=saveusercp`, config ).then( response => {

				if( !response.ok ) {
					response.json().then( json => {
						// Record the Google Analytics event.
						dispatch( trackGAEvent( false, 'saveUserCP_' + settings.field, 'error', 'system' ) );

						setGlobalNotification( dispatch, json, response );
						return resolve( () => ({ result: false }) );
					} );

				} else {
					response.json().then( json => {
						if( json.result === false ) {
							setGlobalNotification( dispatch, json, response );
						} else {
							displayNotification( 'success', 'Setting successfully updated', { force: true, timeout: 1000 } );
							dispatch( setCacheBuster( ( new Date().getTime() ) ) );
						}

						// Record the Google Analytics event.
						settings.forEach( ( field ) => {
							if( field[ 0 ] === 'field' && field[ 1 ] ) {
								json.result === false ?
									dispatch( trackGAEvent( false, 'saveUserCP_' + field[ 1 ], 'error', 'user' ) ) :
									dispatch( trackGAEvent( false, 'saveUserCP_' + field[ 1 ], 'userAction', 'userCP' ) );
							}
						} );

						if( json.forumUser.data[0] ) {
							let currentUser = json.forumUser.data[ 0 ],
								new_ustate = Object.assign( {}, json.ustate, { userName: currentUser.userName, avatar: currentUser.avatar, online: currentUser.online, lastActivity: currentUser.lastActivity } );

							dispatch( receiveCurrentUser( new_ustate ) );

							return resolve( () => ( {
								result: true
							} ) );

						} else {
							return resolve( () => ( {
								result: false
							} ) );
						}
					} );
				}
			} );
		} );
	};
}




// WEBPACK FOOTER //
// ./src/actions/UserprofileActions.js